import { useState, useRef } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { gql } from "../../../__generated__";
import ResponsiveCDNImage from "../../../components/common/image/responsive-cdn-image";
import { notify, notifyError } from "../../../utils/toast";
import LoadingSpinnerInline from "../../../components/common/loading-spinner/loading-spinner-inline";
import {
  AdminBannersQuery,
  CreateBannerMutation,
  UpdateBannerMutation,
} from "../../../__generated__/graphql";

const GET_ADMIN_BANNERS = gql(`
  query AdminBanners {
    adminBanners {
      id
      imageUrl
      link
      address
      alt
      needLogin
      isActive
      sortOrder
      createdAt
      updatedAt
    }
  }
`);

const CREATE_BANNER = gql(`
  mutation CreateBanner($input: CreateBannerInput!) {
    createBanner(input: $input) {
      id
      imageUrl
      link
      address
      alt
      needLogin
      isActive
      sortOrder
    }
  }
`);

const UPDATE_BANNER = gql(`
  mutation UpdateBanner($input: UpdateBannerInput!) {
    updateBanner(input: $input) {
      id
      imageUrl
      link
      address
      alt
      needLogin
      isActive
      sortOrder
    }
  }
`);

const DELETE_BANNER = gql(`
  mutation DeleteBanner($id: Int!) {
    deleteBanner(id: $id) {
      ok
      error
    }
  }
`);

export default function AdminBannerManagement() {
  const { data, loading, error, refetch } =
    useQuery<AdminBannersQuery>(GET_ADMIN_BANNERS);
  const [createBanner] = useMutation<CreateBannerMutation>(CREATE_BANNER);
  const [updateBanner] = useMutation<UpdateBannerMutation>(
    UPDATE_BANNER as any
  );
  const [deleteBanner] = useMutation(DELETE_BANNER as any);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingBanner, setDeletingBanner] = useState<{
    id: number;
    alt: string;
  } | null>(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [viewingImage, setViewingImage] = useState<{
    imageUrl: string;
    alt: string;
  } | null>(null);
  const [editingBanner, setEditingBanner] = useState<{
    id: number;
    imageUrl: string;
    link: string;
    address: string;
    alt: string;
    needLogin: boolean | null;
    isActive: boolean;
    sortOrder: number;
  } | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editFileInputRef = useRef<HTMLInputElement>(null);

  // 폼 상태
  const [formData, setFormData] = useState({
    link: "",
    address: "",
    alt: "",
    needLogin: null as boolean | null,
    isActive: true,
    sortOrder: 0,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  if (loading) return <div className="p-4">로딩 중...</div>;
  if (error)
    return (
      <div className="p-4 text-red-500">
        에러가 발생했습니다: {error.message}
      </div>
    );

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
    }
  };

  const handleRemoveImage = () => {
    setSelectedFile(null);
    setPreviewUrl("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleOpenAddModal = () => {
    setIsAddModalOpen(true);
    setFormData({
      link: "",
      address: "",
      alt: "",
      needLogin: null,
      isActive: true,
      sortOrder: 0,
    });
    setSelectedFile(null);
    setPreviewUrl("");
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
    setFormData({
      link: "",
      address: "",
      alt: "",
      needLogin: null,
      isActive: true,
      sortOrder: 0,
    });
    setSelectedFile(null);
    setPreviewUrl("");
  };

  const handleOpenEditModal = (banner: (typeof banners)[0]) => {
    setEditingBanner({
      id: banner.id,
      imageUrl: banner.imageUrl,
      link: banner.link,
      address: banner.address,
      alt: banner.alt,
      needLogin: banner.needLogin ?? null,
      isActive: banner.isActive,
      sortOrder: banner.sortOrder,
    });
    setFormData({
      link: banner.link,
      address: banner.address,
      alt: banner.alt,
      needLogin: banner.needLogin ?? null,
      isActive: banner.isActive,
      sortOrder: banner.sortOrder,
    });
    setSelectedFile(null);
    setPreviewUrl("");
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingBanner(null);
    setFormData({
      link: "",
      address: "",
      alt: "",
      needLogin: null,
      isActive: true,
      sortOrder: 0,
    });
    setSelectedFile(null);
    setPreviewUrl("");
  };

  const handleEditFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
    }
  };

  const handleRemoveEditImage = () => {
    setSelectedFile(null);
    setPreviewUrl("");
    if (editFileInputRef.current) {
      editFileInputRef.current.value = "";
    }
  };

  const handleUpdateSubmit = async () => {
    if (!editingBanner) return;

    // 유효성 검사
    if (!formData.link.trim()) {
      notifyError("링크를 입력해주세요.");
      return;
    }
    if (!formData.address.trim()) {
      notifyError("주소를 입력해주세요. ('all' 또는 특정 지역명)");
      return;
    }
    if (!formData.alt.trim()) {
      notifyError("alt 텍스트를 입력해주세요.");
      return;
    }

    try {
      setIsSubmitting(true);
      const input: any = {
        id: editingBanner.id,
        link: formData.link.trim(),
        address: formData.address.trim(),
        alt: formData.alt.trim(),
        needLogin: formData.needLogin,
        isActive: formData.isActive,
        sortOrder: formData.sortOrder,
      };

      // 이미지 파일이 선택된 경우 input에 img 추가
      if (selectedFile) {
        input.img = selectedFile;
      }

      await updateBanner({
        variables: {
          input,
        },
        onCompleted: () => {
          notify("배너가 수정되었습니다.");
          handleCloseEditModal();
          refetch();
        },
        onError: (error) => {
          notifyError(error.message || "배너 수정 중 오류가 발생했습니다.");
        },
      });
    } catch (error) {
      notifyError("배너 수정 중 오류가 발생했습니다.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenDeleteModal = (banner: (typeof banners)[0]) => {
    setDeletingBanner({
      id: banner.id,
      alt: banner.alt,
    });
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeletingBanner(null);
  };

  const handleDeleteSubmit = async () => {
    if (!deletingBanner) return;

    try {
      setIsSubmitting(true);
      await deleteBanner({
        variables: {
          id: deletingBanner.id,
        },
        onCompleted: (data) => {
          if (data?.deleteBanner.ok) {
            notify("배너가 삭제되었습니다.");
            handleCloseDeleteModal();
            refetch();
          } else {
            notifyError(
              data?.deleteBanner.error || "배너 삭제 중 오류가 발생했습니다."
            );
          }
        },
        onError: (error) => {
          notifyError(error.message || "배너 삭제 중 오류가 발생했습니다.");
        },
      });
    } catch (error) {
      notifyError("배너 삭제 중 오류가 발생했습니다.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    // 유효성 검사
    if (!selectedFile) {
      notifyError("이미지를 선택해주세요.");
      return;
    }
    if (!formData.link.trim()) {
      notifyError("링크를 입력해주세요.");
      return;
    }
    if (!formData.address.trim()) {
      notifyError("주소를 입력해주세요. ('all' 또는 특정 지역명)");
      return;
    }
    if (!formData.alt.trim()) {
      notifyError("alt 텍스트를 입력해주세요.");
      return;
    }

    try {
      setIsSubmitting(true);
      await createBanner({
        variables: {
          input: {
            img: selectedFile,
            link: formData.link.trim(),
            address: formData.address.trim(),
            alt: formData.alt.trim(),
            needLogin: formData.needLogin,
            isActive: formData.isActive,
            sortOrder: formData.sortOrder,
          },
        },
        onCompleted: () => {
          notify("배너가 생성되었습니다.");
          handleCloseAddModal();
          refetch();
        },
        onError: (error) => {
          notifyError(error.message || "배너 생성 중 오류가 발생했습니다.");
        },
      });
    } catch (error) {
      notifyError("배너 생성 중 오류가 발생했습니다.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const banners = data?.adminBanners || [];

  return (
    <div className="w-full p-4">
      <div className="mb-4 flex justify-between items-center">
        <h1 className="text-2xl font-bold">배너 관리</h1>
        <button
          onClick={handleOpenAddModal}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          배너 추가
        </button>
      </div>

      {banners.length === 0 ? (
        <div className="text-center py-8 text-gray-500">배너가 없습니다.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...banners]
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((banner) => (
              <div
                key={banner.id}
                className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
              >
                {/* 이미지 영역 */}
                <div className="w-full bg-gray-100">
                  <ResponsiveCDNImage
                    src={banner.imageUrl}
                    alt={banner.alt}
                    className="w-full h-auto object-contain cursor-pointer hover:opacity-90 transition-opacity"
                    sizeRatio={1}
                    onClick={() => {
                      setViewingImage({
                        imageUrl: banner.imageUrl,
                        alt: banner.alt,
                      });
                      setIsImageModalOpen(true);
                    }}
                  />
                </div>

                {/* 정보 영역 */}
                <div className="p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="text-xs text-gray-500">
                        ID: {banner.id}
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-500">정렬:</span>
                        <span className="text-3xl font-bold text-blue-600">
                          {banner.sortOrder}
                        </span>
                      </div>
                    </div>
                    <div
                      className={`inline-block px-2 py-1 rounded text-xs ${
                        banner.isActive
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {banner.isActive ? "활성" : "비활성"}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-gray-500 mb-1">Alt</div>
                    <div className="text-sm font-medium text-gray-900">
                      {banner.alt}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-gray-500 mb-1">링크</div>
                    <div className="text-sm text-gray-700 break-all">
                      {banner.link}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-gray-500 mb-1">주소</div>
                    <div className="text-2xl font-bold text-purple-600">
                      {banner.address}
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                    <div className="text-xs text-gray-500">
                      로그인: {banner.needLogin === true ? "필요" : "불필요"}
                    </div>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={() => handleOpenEditModal(banner)}
                      className="flex-1 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                    >
                      수정
                    </button>
                    <button
                      onClick={() => handleOpenDeleteModal(banner)}
                      className="flex-1 px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                    >
                      삭제
                    </button>
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}

      {/* 배너 추가 모달 */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 overflow-y-auto">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-2xl w-full mx-4 my-8">
            <h2 className="text-xl font-bold mb-4">배너 추가</h2>

            {/* 이미지 업로드 */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                이미지 <span className="text-red-500">*</span>
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="w-full p-2 border border-gray-300 rounded"
              />
              {previewUrl && (
                <div className="mt-4 relative inline-block">
                  <img
                    src={previewUrl}
                    alt="미리보기"
                    className="max-w-full h-auto max-h-64 rounded-lg border border-gray-300"
                  />
                  <button
                    onClick={handleRemoveImage}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
                  >
                    ×
                  </button>
                </div>
              )}
            </div>

            {/* 링크 */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                링크 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.link}
                onChange={(e) =>
                  setFormData({ ...formData, link: e.target.value })
                }
                placeholder="/store/741 또는 https://example.com"
                className="w-full p-2 border border-gray-300 rounded"
              />
            </div>

            {/* 주소 */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                주소 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
                placeholder='all 또는 "서울특별시 송파구"'
                className="w-full p-2 border border-gray-300 rounded"
              />
              <p className="text-xs text-gray-500 mt-1">
                "all"을 입력하면 모든 지역에 표시됩니다.
              </p>
            </div>

            {/* Alt 텍스트 */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Alt 텍스트 (amplitude 로깅용){" "}
                <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.alt}
                onChange={(e) =>
                  setFormData({ ...formData, alt: e.target.value })
                }
                placeholder="슈퍼키친 위례 매장 배너"
                className="w-full p-2 border border-gray-300 rounded"
              />
            </div>

            {/* 로그인 필요 여부 */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                로그인 필요 여부
              </label>
              <select
                value={
                  formData.needLogin === null
                    ? ""
                    : formData.needLogin
                    ? "true"
                    : "false"
                }
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData({
                    ...formData,
                    needLogin: value === "" ? null : value === "true",
                  });
                }}
                className="w-full p-2 border border-gray-300 rounded"
              >
                <option value="">모두 표시 (null)</option>
                <option value="true">로그인 필요</option>
                <option value="false">로그인 불필요</option>
              </select>
            </div>

            {/* 활성화 여부 */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                활성화 여부
              </label>
              <select
                value={formData.isActive ? "true" : "false"}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    isActive: e.target.value === "true",
                  })
                }
                className="w-full p-2 border border-gray-300 rounded"
              >
                <option value="true">활성</option>
                <option value="false">비활성</option>
              </select>
            </div>

            {/* 정렬 순서 */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                정렬 순서 (낮을수록 먼저 표시)
              </label>
              <input
                type="number"
                value={formData.sortOrder}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    sortOrder: parseInt(e.target.value) || 0,
                  })
                }
                className="w-full p-2 border border-gray-300 rounded"
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={handleCloseAddModal}
                disabled={isSubmitting}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                취소
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !selectedFile}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {isSubmitting ? <LoadingSpinnerInline /> : "생성"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 배너 수정 모달 */}
      {isEditModalOpen && editingBanner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 overflow-y-auto">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-2xl w-full mx-4 my-8">
            <h2 className="text-xl font-bold mb-4">배너 수정</h2>

            {/* 현재 이미지 */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                현재 이미지
              </label>
              <ResponsiveCDNImage
                src={editingBanner.imageUrl}
                alt={editingBanner.alt}
                className="max-w-full h-auto max-h-64 rounded-lg border border-gray-300"
                sizeRatio={1}
              />
            </div>

            {/* 이미지 변경 (선택사항) */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                이미지 변경 (선택사항)
              </label>
              <input
                ref={editFileInputRef}
                type="file"
                accept="image/*"
                onChange={handleEditFileChange}
                className="w-full p-2 border border-gray-300 rounded"
              />
              {previewUrl && (
                <div className="mt-4 relative inline-block">
                  <img
                    src={previewUrl}
                    alt="미리보기"
                    className="max-w-full h-auto max-h-64 rounded-lg border border-gray-300"
                  />
                  <button
                    onClick={handleRemoveEditImage}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
                  >
                    ×
                  </button>
                </div>
              )}
              <p className="text-xs text-gray-500 mt-1">
                이미지를 변경하지 않으면 현재 이미지가 유지됩니다.
              </p>
            </div>

            {/* 링크 */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                링크 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.link}
                onChange={(e) =>
                  setFormData({ ...formData, link: e.target.value })
                }
                placeholder="/store/741 또는 https://example.com"
                className="w-full p-2 border border-gray-300 rounded"
              />
            </div>

            {/* 주소 */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                주소 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
                placeholder='all 또는 "서울특별시 송파구"'
                className="w-full p-2 border border-gray-300 rounded"
              />
              <p className="text-xs text-gray-500 mt-1">
                "all"을 입력하면 모든 지역에 표시됩니다.
              </p>
            </div>

            {/* Alt 텍스트 */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Alt 텍스트 (amplitude 로깅용){" "}
                <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.alt}
                onChange={(e) =>
                  setFormData({ ...formData, alt: e.target.value })
                }
                placeholder="슈퍼키친 위례 매장 배너"
                className="w-full p-2 border border-gray-300 rounded"
              />
            </div>

            {/* 로그인 필요 여부 */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                로그인 필요 여부
              </label>
              <select
                value={
                  formData.needLogin === null
                    ? ""
                    : formData.needLogin
                    ? "true"
                    : "false"
                }
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData({
                    ...formData,
                    needLogin: value === "" ? null : value === "true",
                  });
                }}
                className="w-full p-2 border border-gray-300 rounded"
              >
                <option value="">모두 표시 (null)</option>
                <option value="true">로그인 필요</option>
                <option value="false">로그인 불필요</option>
              </select>
            </div>

            {/* 활성화 여부 */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                활성화 여부
              </label>
              <select
                value={formData.isActive ? "true" : "false"}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    isActive: e.target.value === "true",
                  })
                }
                className="w-full p-2 border border-gray-300 rounded"
              >
                <option value="true">활성</option>
                <option value="false">비활성</option>
              </select>
            </div>

            {/* 정렬 순서 */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                정렬 순서 (낮을수록 먼저 표시)
              </label>
              <input
                type="number"
                value={formData.sortOrder}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    sortOrder: parseInt(e.target.value) || 0,
                  })
                }
                className="w-full p-2 border border-gray-300 rounded"
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={handleCloseEditModal}
                disabled={isSubmitting}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                취소
              </button>
              <button
                onClick={handleUpdateSubmit}
                disabled={isSubmitting}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {isSubmitting ? <LoadingSpinnerInline /> : "수정"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 배너 삭제 확인 모달 */}
      {isDeleteModalOpen && deletingBanner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4">배너 삭제</h2>
            <p className="text-gray-700 mb-6">
              정말로 배너를 삭제하시겠습니까?
              <br />
              <span className="font-semibold">"{deletingBanner.alt}"</span>
              <br />이 작업은 되돌릴 수 없습니다.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={handleCloseDeleteModal}
                disabled={isSubmitting}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                취소
              </button>
              <button
                onClick={handleDeleteSubmit}
                disabled={isSubmitting}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {isSubmitting ? <LoadingSpinnerInline /> : "삭제"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 이미지 원본 보기 모달 */}
      {isImageModalOpen && viewingImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-50"
          onClick={() => {
            setIsImageModalOpen(false);
            setViewingImage(null);
          }}
        >
          <div className="relative max-w-[90vw] max-h-[90vh] flex items-center justify-center">
            <img
              src={viewingImage.imageUrl}
              alt={viewingImage.alt}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => {
                setIsImageModalOpen(false);
                setViewingImage(null);
              }}
              className="absolute top-4 right-4 bg-white bg-opacity-80 hover:bg-opacity-100 text-gray-800 rounded-full w-10 h-10 flex items-center justify-center text-xl font-bold transition-all"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
